// Linux & DevOps Automation Utility Engine
// Systemd unit generation, visual Cron translations, CIS hardening audits, and deployment scripts

export const LINUX_PRESETS = [
  {
    id: 'nodejs_api',
    name: 'Node.js / Express Production API',
    badge: 'Web Daemon',
    description: 'Production systemd service unit for a Node.js web server with automated restart, non-root user, and cgroup memory limits.',
    config: {
      serviceName: 'express-api',
      description: 'Production Express.js REST API Server',
      execStart: '/usr/bin/node /opt/express-api/server.js',
      execReload: '/bin/kill -s HUP $MAINPID',
      workingDirectory: '/opt/express-api',
      user: 'nodejs-app',
      group: 'nodejs-app',
      serviceType: 'simple',
      restartPolicy: 'always',
      restartSec: 5,
      environmentFile: '/opt/express-api/.env',
      envVars: 'NODE_ENV=production\nPORT=3000',
      memoryMax: '1024M',
      cpuQuota: '150%',
      limitNoFile: 65536,
      protectSystem: 'strict',
      protectHome: true,
      privateTmp: true,
      noNewPrivileges: true,
      afterUnits: 'network.target postgresql.service',
      // Cron config
      cronMinute: '30',
      cronHour: '2',
      cronDom: '*',
      cronMonth: '*',
      cronDow: '*',
      cronPreset: 'daily_230am',
      timerCalendar: '*-*-* 02:30:00'
    }
  },
  {
    id: 'python_fastapi',
    name: 'Python FastAPI / Gunicorn Worker',
    badge: 'Microservice',
    description: 'High-concurrency async Python worker managed by Gunicorn/Uvicorn with virtualenv execution paths and strict sandboxing.',
    config: {
      serviceName: 'fastapi-backend',
      description: 'FastAPI High-Performance Uvicorn Worker',
      execStart: '/opt/fastapi/venv/bin/gunicorn -w 4 -k uvicorn.workers.UvicornWorker main:app --bind 0.0.0.0:8000',
      execReload: '/bin/kill -s HUP $MAINPID',
      workingDirectory: '/opt/fastapi',
      user: 'fastapi-app',
      group: 'fastapi-app',
      serviceType: 'simple',
      restartPolicy: 'on-failure',
      restartSec: 3,
      environmentFile: '/opt/fastapi/.env',
      envVars: 'PYTHONUNBUFFERED=1\nENVIRONMENT=production',
      memoryMax: '2048M',
      cpuQuota: '200%',
      limitNoFile: 65536,
      protectSystem: 'strict',
      protectHome: true,
      privateTmp: true,
      noNewPrivileges: true,
      afterUnits: 'network.target',
      // Cron config
      cronMinute: '0',
      cronHour: '*/4',
      cronDom: '*',
      cronMonth: '*',
      cronDow: '*',
      cronPreset: 'every_4hours',
      timerCalendar: '*-*-* 00/4:00:00'
    }
  },
  {
    id: 'postgres_backup',
    name: 'PostgreSQL Nightly Backup Timer',
    badge: 'Maintenance',
    description: 'Scheduled database snapshot job executed via Systemd Timer with ephemeral oneshot lifecycle and compression.',
    config: {
      serviceName: 'postgres-backup',
      description: 'Automated PostgreSQL Database Snapshot to S3/Local',
      execStart: '/usr/local/bin/pg_dump_backup.sh',
      execReload: '',
      workingDirectory: '/var/backups',
      user: 'postgres',
      group: 'postgres',
      serviceType: 'oneshot',
      restartPolicy: 'no',
      restartSec: 0,
      environmentFile: '',
      envVars: 'PGDATABASE=production_db\nBACKUP_DIR=/var/backups/postgres',
      memoryMax: '512M',
      cpuQuota: '100%',
      limitNoFile: 1024,
      protectSystem: 'full',
      protectHome: true,
      privateTmp: true,
      noNewPrivileges: true,
      afterUnits: 'network.target postgresql.service',
      // Cron config
      cronMinute: '0',
      cronHour: '3',
      cronDom: '*',
      cronMonth: '*',
      cronDow: '0',
      cronPreset: 'weekly_sun_3am',
      timerCalendar: 'Sun *-*-* 03:00:00'
    }
  },
  {
    id: 'go_hardened_daemon',
    name: 'Go Binary with Strict CIS Sandboxing',
    badge: 'Zero-Trust Hardened',
    description: 'Minimal static Go daemon utilizing maximum Linux kernel sandboxing: read-only root, private devices, and dropped capabilities.',
    config: {
      serviceName: 'metrics-collector',
      description: 'Hardened Go Metrics Collector Daemon',
      execStart: '/usr/local/bin/metrics-collector --config /etc/metrics/config.yaml',
      execReload: '',
      workingDirectory: '/tmp',
      user: 'daemon-user',
      group: 'daemon-user',
      serviceType: 'simple',
      restartPolicy: 'always',
      restartSec: 2,
      environmentFile: '',
      envVars: 'GOGC=80',
      memoryMax: '256M',
      cpuQuota: '50%',
      limitNoFile: 16384,
      protectSystem: 'strict',
      protectHome: true,
      privateTmp: true,
      noNewPrivileges: true,
      afterUnits: 'network.target',
      // Cron config
      cronMinute: '*/15',
      cronHour: '*',
      cronDom: '*',
      cronMonth: '*',
      cronDow: '*',
      cronPreset: 'every_15min',
      timerCalendar: '*:00/15:00'
    }
  }
];

export const CRON_PRESETS = [
  { id: 'every_15min', label: 'Every 15 Minutes', minute: '*/15', hour: '*', dom: '*', month: '*', dow: '*', calendar: '*:00/15:00' },
  { id: 'hourly', label: 'Hourly (Top of Hour)', minute: '0', hour: '*', dom: '*', month: '*', dow: '*', calendar: '*:00:00' },
  { id: 'daily_midnight', label: 'Daily at Midnight (00:00)', minute: '0', hour: '0', dom: '*', month: '*', dow: '*', calendar: '*-*-* 00:00:00' },
  { id: 'daily_230am', label: 'Daily at 2:30 AM', minute: '30', hour: '2', dom: '*', month: '*', dow: '*', calendar: '*-*-* 02:30:00' },
  { id: 'weekdays_8am', label: 'Weekdays at 8:00 AM (Mon-Fri)', minute: '0', hour: '8', dom: '*', month: '*', dow: '1-5', calendar: 'Mon..Fri *-*-* 08:00:00' },
  { id: 'weekly_sun_3am', label: 'Weekly on Sunday at 3:00 AM', minute: '0', hour: '3', dom: '*', month: '*', dow: '0', calendar: 'Sun *-*-* 03:00:00' },
  { id: 'monthly_1st', label: 'Monthly on 1st at 00:00', minute: '0', hour: '0', dom: '1', month: '*', dow: '*', calendar: '*-*-01 00:00:00' }
];

// Translate Cron Expression to Plain English
export const translateCronToHuman = (minute, hour, dom, month, dow) => {
  let mText = minute === '*' ? 'every minute' : minute.startsWith('*/') ? `every ${minute.replace('*/', '')} minutes` : `at minute ${minute}`;
  let hText = hour === '*' ? 'every hour' : hour.startsWith('*/') ? `every ${hour.replace('*/', '')} hours` : `at ${hour.padStart(2, '0')}:00`;
  
  if (minute !== '*' && hour !== '*' && !minute.startsWith('*/') && !hour.startsWith('*/')) {
    hText = `at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    mText = '';
  }

  let domText = dom === '*' ? '' : dom.startsWith('*/') ? `every ${dom.replace('*/', '')} days` : `on day ${dom} of the month`;
  let monText = month === '*' ? '' : `in month ${month}`;
  
  const dayNames = { '0': 'Sunday', '1': 'Monday', '2': 'Tuesday', '3': 'Wednesday', '4': 'Thursday', '5': 'Friday', '6': 'Saturday', '7': 'Sunday', '1-5': 'Monday through Friday', '0,6': 'weekends' };
  let dowText = dow === '*' ? '' : `on ${dayNames[dow] || `day-of-week ${dow}`}`;

  const parts = [hText || mText, domText, dowText, monText].filter(Boolean);
  return `Runs ${parts.join(', ')}.`;
};

// Predict Next 5 Execution Timestamps
export const getNextExecutionPreviews = (minute, hour) => {
  const executions = [];
  const now = new Date();
  
  // Clean parse or fallback to current minute/hour
  const m = parseInt(minute) || 0;
  const h = parseInt(hour) || 0;

  for (let i = 0; i < 5; i++) {
    const nextDate = new Date(now.getTime() + (i + 1) * 24 * 3600 * 1000);
    nextDate.setHours(h, m, 0, 0);
    executions.push(nextDate.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }));
  }
  return executions;
};

// Calculate Linux Daemon Hardening Score
export const calculateHardeningScore = (config) => {
  let score = 0;
  const checks = [];

  // Check 1: Non-Root User
  if (config.user && config.user !== 'root') {
    score += 20;
    checks.push({ passed: true, title: 'Non-Root Execution User', desc: `Running daemon under dedicated unprivileged account '${config.user}' prevents system-wide compromise.` });
  } else {
    checks.push({ passed: false, title: 'Root Execution Detected', desc: 'Running as root grants excessive permissions. Create an isolated system user (e.g. useradd -r -s /bin/false app).' });
  }

  // Check 2: NoNewPrivileges
  if (config.noNewPrivileges) {
    score += 20;
    checks.push({ passed: true, title: 'NoNewPrivileges=true Enabled', desc: 'Prevents child processes from gaining elevated permissions via setuid binaries (e.g. sudo).' });
  } else {
    checks.push({ passed: false, title: 'NoNewPrivileges Disabled', desc: 'Set NoNewPrivileges=true to block setuid privilege escalation vulnerabilities.' });
  }

  // Check 3: ProtectSystem
  if (config.protectSystem === 'strict' || config.protectSystem === 'full') {
    score += 20;
    checks.push({ passed: true, title: `ProtectSystem=${config.protectSystem} Active`, desc: 'Mounts /usr, /boot, and /etc as read-only for the daemon to prevent filesystem tampering.' });
  } else {
    checks.push({ passed: false, title: 'Filesystem Write Protection Inactive', desc: 'ProtectSystem=strict prevents malicious binaries or config file alterations.' });
  }

  // Check 4: ProtectHome
  if (config.protectHome) {
    score += 15;
    checks.push({ passed: true, title: 'ProtectHome=true Active', desc: 'Hides /home, /root, and /run/user from the service process to protect user credentials.' });
  } else {
    checks.push({ passed: false, title: 'Home Directory Visible', desc: 'ProtectHome=true shields SSH keys and personal user data from being read by the service.' });
  }

  // Check 5: PrivateTmp
  if (config.privateTmp) {
    score += 15;
    checks.push({ passed: true, title: 'PrivateTmp=true Active', desc: 'Allocates an isolated /tmp namespace, preventing symlink attacks and shared temp file leaks.' });
  } else {
    checks.push({ passed: false, title: 'Shared Temp Namespace', desc: 'PrivateTmp=true isolates temporary directories between system processes.' });
  }

  // Check 6: MemoryMax Cgroup
  if (config.memoryMax) {
    score += 10;
    checks.push({ passed: true, title: `Memory Cgroup Enforced (${config.memoryMax})`, desc: 'Prevents runaway memory leaks from triggering system-wide OOM (Out of Memory) kills.' });
  } else {
    checks.push({ passed: false, title: 'No MemoryMax Limit', desc: 'Define MemoryMax (e.g. 1024M) to contain rogue memory consumption within cgroups.' });
  }

  let grade = 'F';
  let badgeColor = '#ff4757';
  if (score >= 90) { grade = 'A+ (CIS Compliant)'; badgeColor = '#2ecc71'; }
  else if (score >= 75) { grade = 'A (Hardened)'; badgeColor = '#00d2d3'; }
  else if (score >= 55) { grade = 'B (Acceptable)'; badgeColor = '#f8d210'; }
  else if (score >= 35) { grade = 'C (Permissive)'; badgeColor = '#ff7675'; }

  return { score, grade, badgeColor, checks };
};

// Generate Systemd Service Unit File
export const generateSystemdService = (config) => {
  const {
    serviceName = 'my-app',
    description = 'My Custom Service',
    execStart = '/usr/bin/node /opt/app/server.js',
    execReload = '',
    workingDirectory = '/opt/app',
    user = 'app-user',
    group = 'app-user',
    serviceType = 'simple',
    restartPolicy = 'always',
    restartSec = 5,
    environmentFile = '',
    envVars = '',
    memoryMax = '1024M',
    cpuQuota = '150%',
    limitNoFile = 65536,
    protectSystem = 'strict',
    protectHome = true,
    privateTmp = true,
    noNewPrivileges = true,
    afterUnits = 'network.target'
  } = config;

  return `# /etc/systemd/system/${serviceName}.service
# Generated by Adedeji Babalola's Linux & DevOps Automation Suite
# Standard: Red Hat Enterprise Linux (RHEL) / systemd 250+

[Unit]
Description=${description}
Documentation=https://github.com/dejoo10/Adedejiwebapp
After=${afterUnits}
Wants=${afterUnits.split(' ')[0] || 'network.target'}

[Service]
Type=${serviceType}
User=${user}
Group=${group}
WorkingDirectory=${workingDirectory}

# Process Execution
ExecStart=${execStart}
${execReload ? `ExecReload=${execReload}` : '# ExecReload not specified'}
Restart=${restartPolicy}
RestartSec=${restartSec}

# Resource Controls & Cgroups
LimitNOFILE=${limitNoFile}
${memoryMax ? `MemoryMax=${memoryMax}` : '# MemoryMax unconstrained'}
${cpuQuota ? `CPUQuota=${cpuQuota}` : '# CPUQuota unconstrained'}

# Environment Configuration
${environmentFile ? `EnvironmentFile=${environmentFile}` : '# EnvironmentFile not defined'}
${envVars ? envVars.split('\n').filter(Boolean).map(line => `Environment="${line.trim()}"`).join('\n') : '# Inline environment variables'}

# Security Sandboxing & CIS Hardening
NoNewPrivileges=${noNewPrivileges ? 'true' : 'false'}
ProtectSystem=${protectSystem}
ProtectHome=${protectHome ? 'true' : 'false'}
PrivateTmp=${privateTmp ? 'true' : 'false'}
PrivateDevices=true
ProtectKernelTunables=true
ProtectKernelModules=true
ProtectControlGroups=true
RestrictRealtime=true
RestrictSUIDSGID=true

# Standard Output / Logging via Journald
StandardOutput=journal
StandardError=journal
SyslogIdentifier=${serviceName}

[Install]
WantedBy=multi-user.target
`;
};

// Generate Modern Systemd Timer Unit File
export const generateSystemdTimer = (config) => {
  const {
    serviceName = 'my-task',
    description = 'Scheduled Automation Task',
    timerCalendar = '*-*-* 02:30:00'
  } = config;

  return `# /etc/systemd/system/${serviceName}.timer
# Modern systemd scheduled task replacing legacy crontab
# Runs paired ${serviceName}.service unit

[Unit]
Description=Timer Schedule for ${description}
RefuseManualStart=no
RefuseManualStop=no

[Timer]
OnCalendar=${timerCalendar}
Persistent=true
RandomizedDelaySec=60
Unit=${serviceName}.service

[Install]
WantedBy=timers.target
`;
};

// Generate Idempotent Bash Deployment Script
export const generateDeploymentBashScript = (config) => {
  const {
    serviceName = 'my-app',
    user = 'app-user',
    group = 'app-user',
    workingDirectory = '/opt/app'
  } = config;

  return `#!/usr/bin/env bash
# ==============================================================================
# Production Service Provisioning & Daemon Setup
# Generated by Adedeji Babalola's Linux & DevOps Automation Suite
# Compatible with RHEL 8/9, Rocky Linux, AlmaLinux, Ubuntu 22.04+
# ==============================================================================

set -euo pipefail

# Ensure script is executed with root / sudo privileges
if [[ $EUID -ne 0 ]]; then
   echo "[-] ERROR: This provisioning script must be run as root or with sudo." >&2
   exit 1
fi

echo "[*] Step 1: Provisioning isolated system user and group '${user}'..."
if ! getent group "${group}" >/dev/null 2>&1; then
    groupadd -r "${group}"
    echo "[+] Group '${group}' created."
fi

if ! getent passwd "${user}" >/dev/null 2>&1; then
    useradd -r -g "${group}" -d "${workingDirectory}" -s /sbin/nologin -c "${serviceName} service account" "${user}"
    echo "[+] System user '${user}' created with nologin shell."
fi

echo "[*] Step 2: Preparing application directory structure at '${workingDirectory}'..."
mkdir -p "${workingDirectory}"
mkdir -p "/var/log/${serviceName}"

# Secure file permissions (Least Privilege)
chown -R "${user}:${group}" "${workingDirectory}"
chown -R "${user}:${group}" "/var/log/${serviceName}"
chmod 750 "${workingDirectory}"
chmod 750 "/var/log/${serviceName}"

echo "[*] Step 3: Installing systemd service unit into /etc/systemd/system/${serviceName}.service..."
cat << 'EOF' > "/etc/systemd/system/${serviceName}.service"
${generateSystemdService(config).trim()}
EOF

chmod 644 "/etc/systemd/system/${serviceName}.service"
echo "[+] Service unit written and permissions restricted."

echo "[*] Step 4: Configuring automated log rotation in /etc/logrotate.d/${serviceName}..."
cat << 'EOF' > "/etc/logrotate.d/${serviceName}"
/var/log/${serviceName}/*.log {
    daily
    missingok
    rotate 14
    compress
    delaycompress
    notifempty
    create 0640 ${user} ${group}
    sharedscripts
    postrotate
        /bin/systemctl kill -s HUP ${serviceName}.service >/dev/null 2>&1 || true
    endscript
}
EOF

echo "[*] Step 5: Reloading systemd daemon and enabling ${serviceName}..."
systemctl daemon-reload
systemctl enable --now "${serviceName}.service"

echo "[*] Step 6: Verifying operational status..."
systemctl is-active --quiet "${serviceName}.service" && {
    echo "[+] SUCCESS: ${serviceName}.service is active and running!"
    systemctl status "${serviceName}.service" --no-pager
} || {
    echo "[-] WARNING: ${serviceName}.service failed to start. Inspect logs via 'journalctl -u ${serviceName} -xe'."
    exit 1
}
`;
};
